import React from "react";
import { CcButton, CcIconButton } from "../mui";
import { getBadgeToneStyle } from "../badgeStyles";
import { CcIconCheck, CcIconTrash } from "../../icons";
import { cx } from "../utils";

/**
 * CcVersionCard — care plan / assessment version entry (Carecentre editorial).
 * Mirrors CcEvaluationCard density with version-specific header + actions.
 */
const CcVersionCard = ({
  versionNumber,
  status,
  statusTone = "info",
  isCurrent = false,
  isViewing = false,
  reason,
  note,
  footer = [],
  sectionsChanged = [],
  showLoadButton = false,
  onLoad,
  showDeleteButton = false,
  onDelete,
  className,
}) => {
  const toneStyle = getBadgeToneStyle(statusTone);
  const hasFooter = footer.length > 0;
  const hasSections = sectionsChanged.length > 0;

  return (
    <article
      className={cx(
        "cc-version-card",
        isCurrent && "cc-version-card--current",
        isViewing && "cc-version-card--viewing",
        className,
      )}
    >
      <div className="cc-version-card__header">
        <span
          className={cx(
            "cc-version-card__current-icon",
            !isCurrent && "cc-version-card__current-icon--placeholder",
          )}
          aria-hidden
        >
          {isCurrent ? <CcIconCheck size={16} /> : null}
        </span>

        <div className="cc-version-card__title-row">
          <h4 className="cc-version-card__title">Version {versionNumber}</h4>
          {isViewing ? (
            <span className="cc-version-card__badge cc-version-card__badge--viewing">
              Viewing
            </span>
          ) : null}
          {status ? (
            <span
              className={cx(
                "cc-version-card__badge",
                `cc-version-card__badge--${statusTone}`,
              )}
              style={{
                backgroundColor: toneStyle.background,
                color: toneStyle.color,
              }}
            >
              {status}
            </span>
          ) : null}
        </div>

        <div className="cc-version-card__actions">
          {showDeleteButton && onDelete ? (
            <CcIconButton
              label="Delete version"
              variant="danger"
              size="sm"
              onClick={onDelete}
            >
              <CcIconTrash size={16} />
            </CcIconButton>
          ) : null}
          {showLoadButton && onLoad ? (
            <CcButton variant="secondary" size="sm" onClick={onLoad}>
              Load
            </CcButton>
          ) : null}
        </div>
      </div>

      {reason ? (
        <p className="cc-version-card__reason">
          <span className="cc-version-card__label">Reason:</span> {reason}
        </p>
      ) : null}

      {note ? (
        <p className="cc-version-card__note">
          <span className="cc-version-card__label">Note:</span> {note}
        </p>
      ) : null}

      {hasFooter ? (
        <dl className="cc-version-card__footer">
          {footer.map(({ label, value }) =>
            label && value ? (
              <div key={label} className="cc-version-card__meta-row">
                <dt className="cc-version-card__meta-label">{label}</dt>
                <dd className="cc-version-card__meta-value">{value}</dd>
              </div>
            ) : null,
          )}
        </dl>
      ) : null}

      {hasSections ? (
        <div className="cc-version-card__sections">
          <span className="cc-version-card__sections-label">
            Sections changed
          </span>
          <div className="cc-version-card__sections-chips">
            {sectionsChanged.map((name, idx) => (
              <span key={idx} className="cc-version-card__section-chip">
                {name}
              </span>
            ))}
          </div>
        </div>
      ) : null}
    </article>
  );
};

export default CcVersionCard;
